/**
 * Unified dynamic HTML sanitizer and renderer.
 * All runtime HTML injection should go through setSafeInnerHTML.
 */

const BLOCKED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'meta',
  'link',
  'base'
]);

const PROFILE_RULES = {
  markdown: {
    tags: new Set([
      'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'strong', 'em', 'del',
      'blockquote',
      'ul', 'ol', 'li',
      'hr',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'pre', 'code',
      'a', 'img'
    ]),
    attrs: {
      '*': new Set(['class']),
      a: new Set(['href', 'target', 'rel']),
      img: new Set(['src', 'alt', 'loading', 'referrerpolicy']),
      code: new Set(['class'])
    },
    allowDataAttrs: false
  },
  app: {
    tags: new Set([
      'div', 'span', 'p', 'br', 'small', 'strong', 'em', 'del', 'code', 'pre',
      'ul', 'ol', 'li', 'blockquote',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'article', 'header', 'footer', 'main', 'section', 'nav', 'form', 'time', 'mark',
      'button', 'label', 'input', 'textarea', 'select', 'option',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a', 'img', 'video', 'audio', 'source', 'hr',
      'canvas',
      'svg', 'path', 'rect', 'circle', 'line', 'polyline', 'polygon', 'ellipse'
    ]),
    attrs: {
      '*': new Set([
        'class', 'id', 'title', 'role', 'tabindex',
        'type', 'value', 'placeholder', 'name', 'for',
        'selected', 'checked', 'multiple', 'accept', 'readonly', 'autofocus',
        'min', 'max', 'step',
        'width', 'height', 'alt', 'style',
        'disabled', 'controls', 'preload',
        'colspan', 'rowspan', 'aria-label', 'aria-hidden', 'aria-expanded', 'aria-busy'
      ]),
      a: new Set(['href', 'target', 'rel']),
      img: new Set(['src', 'alt', 'loading', 'referrerpolicy', 'width', 'height']),
      video: new Set(['src', 'controls', 'preload', 'poster']),
      audio: new Set(['src', 'controls', 'preload']),
      source: new Set(['src', 'type']),
      svg: new Set(['viewbox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'width', 'height']),
      path: new Set(['d', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin']),
      rect: new Set(['x', 'y', 'width', 'height', 'rx', 'ry', 'fill', 'stroke', 'stroke-width']),
      circle: new Set(['cx', 'cy', 'r', 'fill', 'stroke', 'stroke-width']),
      line: new Set(['x1', 'y1', 'x2', 'y2', 'fill', 'stroke', 'stroke-width']),
      polyline: new Set(['points', 'fill', 'stroke', 'stroke-width']),
      polygon: new Set(['points', 'fill', 'stroke', 'stroke-width']),
      ellipse: new Set(['cx', 'cy', 'rx', 'ry', 'fill', 'stroke', 'stroke-width'])
    },
    allowDataAttrs: true
  }
};

function normalizeAttrName(attrName) {
  return String(attrName || '').toLowerCase();
}

function isAriaAttr(attrName) {
  return /^aria-[a-z0-9-]+$/.test(attrName);
}

function isDataAttr(attrName) {
  return /^data-[a-z0-9-]+$/.test(attrName);
}

function isEventAttr(attrName) {
  return /^on[a-z]+/.test(attrName);
}

function decodeBasicHtmlEntities(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function stripControlAndWhitespace(value) {
  let result = '';
  const input = String(value || '');
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    const code = ch.charCodeAt(0);
    const isControl = (code >= 0 && code <= 31) || code === 127;
    if (isControl || /\s/.test(ch)) {
      continue;
    }
    result += ch;
  }
  return result;
}

function isSafeDataMediaUrl(value, tagName) {
  const text = String(value || '').toLowerCase();
  const mediaType = tagName === 'img'
    ? /^data:image\/(png|jpeg|jpg|gif|webp|avif|bmp|x-icon);base64,[a-z0-9+/=\s]+$/
    : tagName === 'audio'
      ? /^data:audio\/(mpeg|mp3|ogg|wav|webm);base64,[a-z0-9+/=\s]+$/
      : tagName === 'source'
        ? /^data:(audio\/(mpeg|mp3|ogg|wav|webm)|video\/(mp4|webm|ogg));base64,[a-z0-9+/=\s]+$/
        : /^data:video\/(mp4|webm|ogg);base64,[a-z0-9+/=\s]+$/;
  return mediaType.test(text);
}

function sanitizeUrl(url, options = {}) {
  const {
    allowMailto = true,
    allowBlob = false,
    allowDataMedia = false,
    dataMediaTagName = 'img'
  } = options;
  const decoded = decodeBasicHtmlEntities(url);
  const normalized = stripControlAndWhitespace(decoded.trim());
  if (!normalized) return '';
  const raw = normalized.startsWith('//') ? `https:${normalized}` : normalized;
  if (allowBlob && /^blob:/i.test(raw)) {
    return raw;
  }
  if (allowDataMedia && /^data:/i.test(raw) && isSafeDataMediaUrl(raw, dataMediaTagName)) {
    return raw;
  }
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://localhost');
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:' || (allowMailto && parsed.protocol === 'mailto:')) {
      return parsed.toString();
    }
  } catch {
  }
  return '';
}

function getProfileRules(profile) {
  return PROFILE_RULES[profile] || PROFILE_RULES.app;
}

function sanitizeElementAttributes(el, rules) {
  const tagName = el.tagName.toLowerCase();
  const globalAttrs = rules.attrs['*'] || new Set();
  const tagAttrs = rules.attrs[tagName] || new Set();

  Array.from(el.attributes).forEach((attr) => {
    const attrName = normalizeAttrName(attr.name);
    const rawValue = attr.value;

    if (isEventAttr(attrName)) {
      el.removeAttribute(attr.name);
      return;
    }

    const allowByName = globalAttrs.has(attrName)
      || tagAttrs.has(attrName)
      || isAriaAttr(attrName)
      || (rules.allowDataAttrs && isDataAttr(attrName));
    if (!allowByName) {
      el.removeAttribute(attr.name);
      return;
    }

    if (attrName === 'href') {
      const safe = sanitizeUrl(rawValue, {
        allowMailto: true,
        allowBlob: false,
        allowDataMedia: false
      });
      if (!safe) {
        el.removeAttribute(attr.name);
      } else {
        el.setAttribute(attr.name, safe);
      }
      return;
    }

    if (attrName === 'src') {
      const allowDataMedia = tagName === 'img' || tagName === 'audio' || tagName === 'video' || tagName === 'source';
      const safe = sanitizeUrl(rawValue, {
        allowMailto: false,
        allowBlob: true,
        allowDataMedia,
        dataMediaTagName: tagName
      });
      if (!safe) {
        el.removeAttribute(attr.name);
      } else {
        el.setAttribute(attr.name, safe);
      }
      return;
    }

    if (tagName === 'a' && attrName === 'target') {
      const target = rawValue === '_self' ? '_self' : '_blank';
      el.setAttribute('target', target);
      return;
    }

    if (tagName === 'a' && attrName === 'rel') {
      el.setAttribute('rel', 'noopener noreferrer');
      return;
    }

    if (attrName === 'style') {
      const safeStyle = sanitizeInlineStyle(rawValue);
      if (!safeStyle) {
        el.removeAttribute('style');
      } else {
        el.setAttribute('style', safeStyle);
      }
      return;
    }
  });
}

function sanitizeInlineStyle(styleText) {
  const text = String(styleText || '').trim();
  if (!text) return '';
  if (/(expression\s*\(|@import|javascript:|vbscript:|behavior\s*:)/i.test(text)) {
    return '';
  }

  const safeDecls = [];
  text.split(';').forEach((chunk) => {
    const decl = chunk.trim();
    if (!decl) return;
    const index = decl.indexOf(':');
    if (index <= 0) return;
    const prop = decl.slice(0, index).trim().toLowerCase();
    const value = decl.slice(index + 1).trim();
    if (!/^[a-z-]+$/.test(prop)) return;
    if (!value) return;
    if (/(url\s*\(\s*['"]?\s*javascript:|expression\s*\()/i.test(value)) return;
    safeDecls.push(`${prop}: ${value}`);
  });

  return safeDecls.join('; ');
}

function sanitizeNode(node, rules, doc) {
  if (!node) return;

  const children = Array.from(node.childNodes);
  children.forEach((child) => sanitizeNode(child, rules, doc));

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node;
  const tagName = el.tagName.toLowerCase();

  if (BLOCKED_TAGS.has(tagName)) {
    el.remove();
    return;
  }

  if (!rules.tags.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    while (el.firstChild) {
      fragment.appendChild(el.firstChild);
    }
    el.replaceWith(fragment);
    return;
  }

  sanitizeElementAttributes(el, rules);
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function sanitizeDynamicHtml(html, options = {}) {
  const { profile = 'app' } = options;
  const rules = getProfileRules(profile);
  if (!html) return '';
  if (typeof DOMParser === 'undefined') {
    return escapeHtml(html);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;
  Array.from(body.childNodes).forEach((node) => sanitizeNode(node, rules, doc));
  return body.innerHTML;
}

export function setSafeInnerHTML(targetEl, html, options = {}) {
  if (!targetEl) return '';
  const safe = sanitizeDynamicHtml(html, options);
  targetEl.innerHTML = safe;
  return safe;
}

export function sanitizeUserUrl(url) {
  return sanitizeUrl(url, {
    allowMailto: true,
    allowBlob: false,
    allowDataMedia: false
  });
}
